import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  ariaLabel?: string;
}

type MenuPosition = {
  top: number;
  left: number;
  width: number;
};

const sizeClassMap = {
  sm: 'h-10 px-3 text-sm rounded-xl',
  md: 'h-12 px-4 text-sm rounded-xl',
};

const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = '请选择',
  emptyLabel = '暂无选项',
  className = '',
  disabled = false,
  size = 'md',
  ariaLabel,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0, width: 0 });

  const selectedIndex = useMemo(
    () => options.findIndex(option => option.value === value),
    [options, value]
  );
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(Math.max(rect.width, 180), viewportWidth - 16);
    const estimatedHeight = Math.min(Math.max(options.length, 1) * 44 + 8, 260);
    const shouldOpenUp = viewportHeight - rect.bottom < estimatedHeight && rect.top > viewportHeight - rect.bottom;
    const left = Math.min(Math.max(8, rect.left), viewportWidth - width - 8);
    const top = shouldOpenUp
      ? Math.max(8, rect.top - estimatedHeight - 6)
      : Math.min(viewportHeight - 8, rect.bottom + 6);

    setPosition({ top, left, width });
  }, [options.length]);

  const getNextIndex = useCallback((from: number, direction: 1 | -1) => {
    if (options.length === 0) return -1;

    for (let step = 1; step <= options.length; step += 1) {
      const next = (from + step * direction + options.length) % options.length;
      if (!options[next]?.disabled) return next;
    }
    return -1;
  }, [options]);

  const selectOption = useCallback((option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  }, [onChange]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    setActiveIndex(selectedIndex >= 0 && !options[selectedIndex]?.disabled ? selectedIndex : getNextIndex(-1, 1));

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex(current => getNextIndex(current, 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(current => getNextIndex(current < 0 ? options.length : current, -1));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const option = options[activeIndex];
        if (option) selectOption(option);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [activeIndex, getNextIndex, open, options, selectOption, selectedIndex, updatePosition]);

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      updatePosition();
      setOpen(true);
    }
  };

  const menu = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={menuRef}
      style={{ top: position.top, left: position.left, width: position.width }}
      className="fixed z-[10020] max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg outline-none"
      role="listbox"
      aria-label={ariaLabel || placeholder}
    >
      {options.length === 0 ? (
        <div className="px-3 py-2 text-sm font-medium text-gray-500">{emptyLabel}</div>
      ) : (
        options.map((option, index) => {
          const selected = option.value === value;
          const active = index === activeIndex;

          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={selected}
              disabled={option.disabled}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectOption(option)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                option.disabled
                  ? 'cursor-not-allowed text-gray-300'
                  : active || selected
                    ? 'bg-[#FFF8B8] text-gray-950'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{option.label}</span>
                {option.description && (
                  <span className="mt-0.5 block truncate text-xs font-medium text-gray-500">{option.description}</span>
                )}
              </span>
              <Check className={`h-4 w-4 flex-none ${selected ? 'opacity-100' : 'opacity-0'}`} />
            </button>
          );
        })
      )}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || placeholder}
        onClick={() => {
          if (disabled) return;
          updatePosition();
          setOpen(current => !current);
        }}
        onKeyDown={handleTriggerKeyDown}
        className={`ios-input flex min-w-0 items-center justify-between gap-3 text-left outline-none ${sizeClassMap[size]} ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-white'
        } ${className}`}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedOption ? 'text-gray-950' : 'text-gray-500'}`}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 flex-none text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {menu}
    </>
  );
};

export default Select;
